import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface InterpretResult {
  queryString: string;
  understood: string[];
  usedModel: boolean;
  message?: string | null;
}

/** The examples under the box, which exist to say that a sentence is allowed here at all. */
export const SEARCH_EXAMPLES = [
  'Porodični auto do 10.000 €',
  'Mali gradski auto do 4.000 €',
  'Nemac limuzina dizel, malo jači',
  'Sportski benzinac, kupe',
];

/**
 * One search box for both kinds of search.
 *
 * Every query goes through the interpreter, because there is no way for a buyer to know in advance
 * which kind theirs is. "Audi Q8" is a marque and a model, "50000km" is a number with a unit,
 * "nemac" is a reputation and "crveni kabriolet" is two filters — all four are things people type
 * into the same box, and asking them to first classify their own sentence and pick the matching
 * field was the whole problem with having two.
 *
 * What comes back is a query string, so the result is an ordinary search: refinable in the panel,
 * shareable as a link, and reversible with the back button. Anything the interpreter could not read
 * comes back as a free-text term instead, which is exactly what the old keyword box did — so the
 * merge loses nothing.
 */
export function useSmartSearch(apply: (params: URLSearchParams) => void) {
  const interpret = useMutation({
    mutationFn: (text: string) =>
      api<InterpretResult>('/api/search/interpret', { method: 'POST', body: { prompt: text } }),
    onSuccess: (data, text) => {
      const params = new URLSearchParams(data.queryString);

      /*
        The sentence is only carried alongside when it became filters. Where it did not, the
        interpreter has already put it in `q` and the box reads it back from there — keeping both
        would show the same words twice, once in the box and once as a chip under it.
      */
      if (!params.has('q')) {
        params.set('qs', text);
      }

      apply(params);
    },
    // The interpreter being unreachable must not mean the search button does nothing. Falling back
    // to a plain keyword search is exactly what the old second box would have done with the words.
    onError: (_error, text) => apply(new URLSearchParams({ q: text })),
  });

  const search = (text: string) => {
    const trimmed = text.trim();

    if (trimmed.length > 0 && !interpret.isPending) {
      interpret.mutate(trimmed);
    }
  };

  return {
    search,
    pending: interpret.isPending,
    // Said out loud rather than left as an empty result set: "we could not read this, so we looked
    // for the words instead" is the difference between a search that failed and a site with no cars.
    message: interpret.isError
      ? 'Nismo uspeli da pročitamo pretragu — tražimo po tekstu.'
      : (interpret.data?.message ?? null),
  };
}
